import DownloadIcon from "@mui/icons-material/Download";
import SaveIcon from "@mui/icons-material/Save";
import UploadFileIcon from "@mui/icons-material/UploadFile";
import { LoadingButton } from "@mui/lab";
import { Box, Grid, Stack, Typography } from "@mui/material";
import { GridCellParams, GridColDef } from "@mui/x-data-grid";
import { FileSection, FormDatePicker, FormTextField } from "components/atoms";
import AppModal from "components/atoms/AppModal";
import { DetailTableTabs, ReportHeader } from "components/molecules";
import { FormikHelpers, useFormik } from "formik";
import {
  DialogHelper,
  DownloadHelper,
  RoleHelper,
  StatusHelper,
  TimeHelper,
} from "helpers";
import { useRequest } from "hooks/useRequest";
import {
  EReceiveStatus,
  IDownloadReceiveDocumentByIdRequest,
  IGetByIdRequest,
  IReceiveDetail,
  IReceiveDocument,
  IUpdateReceiveDocument,
} from "model";
import React, { createRef, useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { DownloadActions, ReceiveActions } from "redux/actions";
import {
  useAppDispatch,
  useAppSelector,
} from "redux/configurations/reduxHooks";
import ReceiveApiService from "services/ReceiveApiService";
import { EXPORT_FILE_NAME } from "utils/Const";
import i18n, { I18nTranslator, translations, _t } from "utils/i18n";
import { ImportPalletDetail, ReceiveDocumentDetail } from "./Components";

const MenuProps = {
  PaperProps: {
    style: {
      width: 250,
    },
  },
};

const _fileId = "import-receive-document";

interface ReceiveDetailProps<T> {
  title?: string;
  loading?: boolean;
  columns: GridColDef[];
  pageSize?: number;
  onCellClick?(params: GridCellParams<any, any, any>): void;
  onAddNewClick?(): void;
  onSearch(searchValue: string): void;
  onChangePage(pageNumber: number): void;
  headerButtons?: React.ReactNode;
  onPrint: () => void;
  onConfirm: () => void;
}

const ReceiveDetail = <T extends { [key: string]: any }>(
  props: ReceiveDetailProps<T>
) => {
  const fileRef = React.useRef<any>();

  const params = useParams<{
    stockReceivingHeaderId: string;
  }>();
  const tabRef = createRef<any>();
  const dispatch = useAppDispatch();
  const [confirmModal, setConfirmModal] = useState(false);

  const action = useAppSelector((state) => state.ReceiveReducer.action);
  const [currentReceiveDetail, setCurrentReceiveDetail] =
    useState<IReceiveDetail>();

  const currentWarehouse = useAppSelector(
    (state) => state.IdentityReducer.currentWarehouse
  );


  const handleFileSelected = (files: FileList) => {
    if (files) {
      const file = files.item(0);
      DialogHelper.openConfirmImportDialog({
        title: "Nhập phiếu nhập hàng",
        fileName: file?.name!,
        onConfirm: () =>
          dispatch(
            ReceiveActions.importReceiveDocument.request({
              stockReceivingHeaderId: params.stockReceivingHeaderId,
              file: file as File,
            })
          ),
      });
    }
  };

  const tabsName = [
    I18nTranslator(translations.receive.documentDetail),
    I18nTranslator(translations.receive.importPalletDetail),
  ];

  const tabsPanel = [<ReceiveDocumentDetail />, <ImportPalletDetail />];

  return (
    <>
      <FileSection
        ref={fileRef}
        id={_fileId}
        onFileSelected={handleFileSelected}
      />
      <div
        style={{
          display: "flex",
          flex: 1,
          flexDirection: "column",
          height: "100%",
        }}
      >
        <DetailHeader />
        <DetailTableTabs
          tabsName={tabsName}
          tabsPanel={tabsPanel}
          hideHeaderButton
        />
      </div>
      <AppModal
        open={confirmModal}
        title={i18n.t(_t(translations.receive.confirmation))}
        onCancel={() => setConfirmModal(false)}
        onOk={() => setConfirmModal(false)}
      >
        {i18n.t(_t(translations.receive.confirmationDescription))}
      </AppModal>
    </>
  );
};

const DetailHeader = () => {
  const params = useParams<{
    stockReceivingHeaderId: string;
    stockReceivingCode: string;
  }>();
  const dispatch = useAppDispatch();
  const action = useAppSelector((state) => state.ReceiveReducer.action);
  const downloading = useAppSelector(
    (state) => state.AppConfigReducer.downloading
  );

  const [refresh, setRefresh] = useState(false);
  const [request] = useState({ id: params.stockReceivingHeaderId });

  const { loading, data } = useRequest<IGetByIdRequest, IReceiveDocument>(
    request,
    ReceiveApiService.getReceiveDocumentById,
    [],
    {
      refresh: refresh,
    },
    () => {
      setRefresh(false);
    }
  );

  const formik = useFormik<IReceiveDocument>({
    initialValues: data || ({} as IReceiveDocument),
    enableReinitialize: true,
    onSubmit: (
      values: IReceiveDocument,
      { validateForm }: FormikHelpers<IReceiveDocument>
    ) => {
      validateForm().then(() => {
        const request: IUpdateReceiveDocument = {
          id: values.id!,
          userID: values.userID!,
          stockReceivingCode: values.stockReceivingCode!,
          deliveryDate: values.deliveryDate!,
          companyName: values.companyName!,
          truckNo: values.truckNo!,
          driver: values.driver!,
          description: values.description!,
          materialType: values.materialType!,
          verifyStatus: values.verifyStatus!,
          wmApproveImportDate: values.wmApproveImportDate!,
          warehouseManagerUserID: values.warehouseManagerUserID!,
          importStatus: values.importStatus!,
          userName: values.userName!,
          createdBySitemapID: values.createdBySitemapID!,
          updatedBySitemapID: values.updatedBySitemapID!,
          quantityReceived: values.quantityReceived!,
          quantityPlanning: values.quantityPlanning!,
        };
        dispatch(ReceiveActions.updateReceiveDocument.request(request));
      });
    },
  });

  const onDownloadReceiveDocument = async () => {
    const request: IDownloadReceiveDocumentByIdRequest = {
      id: params.stockReceivingHeaderId,
      fileName: `${EXPORT_FILE_NAME.receiveDetail}_[${params.stockReceivingCode}]`,
    };
    dispatch(DownloadActions.downloadReceiveDocumentById.request(request));
  };

  useEffect(() => {
    if (action.includes(ReceiveActions.importReceiveDocument.successName)) {
      setRefresh(true);
      dispatch(ReceiveActions.clearAction.request());
    }
  }, [action]);
  return (
    <ReportHeader
      title={I18nTranslator(translations.receive.receiveDocsDetail)}
      description={params.stockReceivingCode}
      formik={formik}
      loading={
        loading ||
        action.includes(ReceiveActions.updateReceiveDocument.requestName)
      }
      confirmButtonText={I18nTranslator(translations.button.save)}
      confirmButtonIcon={<SaveIcon />}
      renderButtons={
        <Stack direction={"row"} spacing={2}>
          <label htmlFor={_fileId}>
            <LoadingButton
              component={"span"}
              loading={action.includes(
                ReceiveActions.importReceiveDocument.requestName
              )}
              startIcon={<UploadFileIcon />}
              variant="contained"
              disabled={
                !(
                  RoleHelper.isSuperAdmin() ||
                  RoleHelper.isAdmin() ||
                  RoleHelper.isPlanner()
                )
              }
            >
              {i18n.t(_t(translations.receive.importDocs))}
            </LoadingButton>
          </label>
          <LoadingButton
            loading={downloading}
            startIcon={<DownloadIcon />}
            onClick={onDownloadReceiveDocument}
            variant="contained"
            disabled={
              !(
                RoleHelper.isSuperAdmin() ||
                RoleHelper.isAdmin() ||
                RoleHelper.isPlanner()
              )
            }
          >
            {i18n.t(_t(translations.button.exportDocs))}
          </LoadingButton>
        </Stack>
      }
    >
      <Grid container xs={12} item columnSpacing={2}>
        <Grid xs={6} item>
          <FormTextField
            label={I18nTranslator(translations.receive.vendor)}
            placeholder={I18nTranslator(translations.placeholder.vendor)}
            id="companyName"
          />
        </Grid>
        <Grid xs={6} item>
          <FormTextField
            label={I18nTranslator(translations.receive.importReceiveStatus)}
            id="importStatus"
            disabled
            renderValue={() =>
              StatusHelper.getReceiveStatusString(
                formik.values.importStatus as EReceiveStatus
              )
            }
          />
        </Grid>
      </Grid>
      <Grid container xs={12} item columnSpacing={2}>
        <Grid xs={6} item>
          <FormDatePicker
            id="deliveryDate"
            label={I18nTranslator(translations.receive.importReceiveDate)}
            defaultValue={TimeHelper.formatDate(formik.values.deliveryDate)}
          />
        </Grid>
        <Grid xs={6} item>
          <FormTextField
            label={I18nTranslator(translations.receive.verifyReceiveStatus)}
            id="verifyStatus"
            disabled
            renderValue={() =>
              StatusHelper.getReceiveStatusString(
                formik.values.verifyStatus as EReceiveStatus
              )
            }
          />
        </Grid>
      </Grid>
      <Grid container xs={12} item columnSpacing={2}>
        <Grid xs={6} item>
          <FormTextField
            label={I18nTranslator(translations.receive.note)}
            placeholder={I18nTranslator(translations.placeholder.note)}
            id="description"
          />
        </Grid>
        <Grid xs={6} item>
          <FormTextField
            label={I18nTranslator(translations.receive.createReceiveName)}
            id="userName"
            disabled
          />
        </Grid>
      </Grid>
    </ReportHeader>
  );
};

interface TabPanelProps {
  children?: React.ReactNode;
  index: number;
  value: number;
}

function TabPanel(props: TabPanelProps) {
  const { children, value, index, ...other } = props;

  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`simple-tabpanel-${index}`}
      aria-labelledby={`simple-tab-${index}`}
      {...other}
    >
      {value === index && (
        <Box sx={{ p: 3 }}>
          <Typography>{children}</Typography>
        </Box>
      )}
    </div>
  );
}

function a11yProps(index: number) {
  return {
    id: `simple-tab-${index}`,
    "aria-controls": `simple-tabpanel-${index}`,
  };
}

export default ReceiveDetail;
